import * as React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Link } from 'react-router-dom';
import * as AccountNotificationState from '../../store/AccountNotifications';
import { fetchApi } from '../../services/api';
import { LoadOneReconnectPolicy } from '../../services/signalr/retryPolicy';
import { ApplicationState } from '../../store';
import { openWindow } from '../../services/openWindow';
import { HubConnection, HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import * as Sentry from '@sentry/react';
import { version } from '../../version';
import { ILink } from '../../types/link';
import { paths } from '../../routes';
import GlobalSearch from '../GlobalSearch';
import ProblemDialog from './ProblemDialog';
import WhatsNewDialog from '../WhatsNew';
import { latestChangelogDate } from '../WhatsNew/changelog';
import { trackEvent } from '../../services/metrics';
import { SvgIcon } from "@progress/kendo-react-common";
import { bellIcon, hyperlinkOpenIcon, rocketLaunchIcon } from '@progress/kendo-svg-icons';
import tinycon from 'tinycon';
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";

interface WebNotification {
  Id: string;
  CreatedDateTime: string;
  Source: number;
  SourceEntity: number;
  ForUser: string;
  Text: string;
  Color: string;
  Sound: string;
  Link: string;
  PopupLink: string;
  Dismissed: boolean;
}

const connector = connect(
  (state: ApplicationState) => state.accountNotifications,
  AccountNotificationState.actionCreators
)

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = PropsFromRedux;

interface State {
  id: string;
  username: string;
  loading: boolean;
  sound: string;
  links: ILink[];
  whatsNewOpen: boolean;
  whatsNewLastSeen: string;
}

class NavBar extends React.Component<Props, State> {

  private connection: HubConnection | null;

  private getActiveNotifications(notifications: Props['accountNotifications'] | null | undefined) {
    if (!notifications) return [];
    return notifications.filter((notification) => notification.Links.some(x => x.Name === 'Dismiss'));
  }

  constructor(props: Props) {
    super(props);

    this.connection = null;

    this.state = {
      id: '',
      username: '',
      loading: true,
      sound: '',
      links: [],
      whatsNewOpen: false,
      whatsNewLastSeen: localStorage.getItem("WhatsNew_LastSeen") ?? '',
    };

    this.dismissAllNotifications = this.dismissAllNotifications.bind(this);
    this.dismissNotification = this.dismissNotification.bind(this);
    this.openAndAcknowledgeNotification = this.openAndAcknowledgeNotification.bind(this);
    this.createSignalRConnection = this.createSignalRConnection.bind(this);
    this.emailSupport = this.emailSupport.bind(this);
    this.openWhatsNew = this.openWhatsNew.bind(this);
  }

  public componentDidMount() {
    fetchApi('/api/Account/Info')
      .then((response: { Id: string, Username: string, Links: ILink[] }) => {
        this.setState({
          loading: false,
          id: response.Id,
          username: response.Username,
          links: response.Links,
        });

        Sentry.setUser({ id: response.Id, email: response.Username });
      });

    // Initial Fetch
    this.props.requestAccountNotifications();

    this.createSignalRConnection();
  }

  public componentWillUnmount() {
    if (this.connection) {
      this.connection.stop();
    }
  }

  public componentDidUpdate(prevProps: Props) {
    const prevActiveCount = this.getActiveNotifications(prevProps.accountNotifications).length;
    const activeCount = this.getActiveNotifications(this.props.accountNotifications).length;

    if (activeCount !== prevActiveCount) {
      tinycon.setBubble(activeCount);
    }
  }

  public render() {
    const activeNotifications = this.getActiveNotifications(this.props.accountNotifications);
    const modalNotification = activeNotifications.find((x) => x.IsModal);
    const hasUnseenChangelog = latestChangelogDate !== '' && latestChangelogDate > this.state.whatsNewLastSeen;

    return <React.Fragment>
      {this.props.newVersion && <div className="p-3 bg-warning">
        <a href="#" onClick={(e) => { e.preventDefault(); window.location.reload(); }}>
          <div className="container text-dark font-weight-bold">New Version Available. Click Here.</div>
        </a>
      </div>}
      {window.location.hostname === 'test.load1.com' && <div style={{ backgroundColor: 'yellow', color: 'black' }}>
        <div style={{ height: 8, background: 'repeating-linear-gradient(45deg, yellow, yellow 10px, black 10px, black 20px)' }}></div>
        <div className="container font-weight-bold py-2">You are on the staging site (test.load1.com).</div>
        <div style={{ height: 8, background: 'repeating-linear-gradient(45deg, yellow, yellow 10px, black 10px, black 20px)' }}></div>
      </div>}
      {this.props.problem && <ProblemDialog problem={this.props.problem} onClose={this.props.dismissProblem} />}
      {this.state.whatsNewOpen && <WhatsNewDialog onClose={() => this.setState({ whatsNewOpen: false })} />}
      {modalNotification && (
        <Dialog
          title={modalNotification.Text}
          closeIcon={null}
          className="dialog-w800"
        >
          {modalNotification.Body}
          <DialogActionsBar layout="end">
            <Button onClick={() => this.dismissNotification(modalNotification)}>
              Acknowledge
            </Button>
            {(modalNotification.PopupLink || modalNotification.Link) && (
              <Button
                themeColor="primary"
                svgIcon={hyperlinkOpenIcon}
                onClick={() => this.openAndAcknowledgeNotification(modalNotification)}
              >
                Open &amp; Acknowledge
              </Button>
            )}
          </DialogActionsBar>
        </Dialog>
      )}
      <nav className="navbar navbar-expand-lg navbar-dark bg-dark">
        <Link to='/' className="navbar-brand">Load One LLC</Link>
        {this.state.sound && <audio src={this.state.sound} onEnded={() => this.setState({ sound: '' })} autoPlay />}
        <ul className="navbar-nav navbar-utility flex-row align-items-center ml-auto order-lg-last">
          <li className="nav-item d-none d-lg-block">
            <button
              type="button"
              className="navbar-pill"
              title="What's New"
              style={{ position: 'relative' }}
              onClick={this.openWhatsNew}
            >
              <SvgIcon icon={rocketLaunchIcon} />
              {hasUnseenChangelog && <span
                style={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: 'red',
                  border: '2px solid #343a40',
                }}
              />}
            </button>
          </li>
          <GlobalSearch />
          <li className="nav-item d-lg-none">
            <button
              className="navbar-toggler"
              type="button"
              data-toggle="collapse"
              data-target="#navbarSupportedContent"
              aria-controls="navbarSupportedContent"
              aria-expanded="false"
              aria-label="Toggle navigation"
            >
              <span className="navbar-toggler-icon" />
            </button>
          </li>
          {!this.state.loading && <li className="dropdown">
            <a
              href="#"
              className="dropdown-toggle"
              id="navbarDropdownAccount"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            >
              {this.state.id && <img className="rounded-circle" title={this.state.username} height="34" src={`/api/User/Photo/${this.state.id}`} />}
              <span className="caret" />
            </a>
            <ul className="dropdown-menu dropdown-menu-right" aria-labelledby="navbarDropdownAccount">
              <Link className="dropdown-item" to={paths.CompanySettings}>Company Settings</Link>
              <Link className="dropdown-item" to={paths.QuoteAutomationRules}>Quote Rules</Link>
              <Link className="dropdown-item" to={paths.AccountSettings}>User Settings</Link>
              <Link className="dropdown-item" to={paths.UserTeams}>User Teams</Link>
              {this.state.links.find(x => x.Name == "Announcements") && <Link className="dropdown-item" to={paths.AnnouncementCreate}>Announcements</Link>}
              <Link className="dropdown-item" to={paths.KnowledgeBase}>Knowledge Base</Link>
              <Link className="dropdown-item" to={paths.AgentNotes}>Agent Notes</Link>
              <a className="dropdown-item" onClick={this.emailSupport}>Email Support</a>
              <a className="dropdown-item" href="/Account/LogOff">Logout</a>
              <div className="dropdown-divider"></div>
              <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); this.openWhatsNew(); }}>What&apos;s New</a>
              <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); window.location.reload(); }}>Client {version}</a>
            </ul>
          </li>}
        </ul>

        <div className="collapse navbar-collapse" id="navbarSupportedContent">
          <ul className="navbar-nav mr-auto">
            {this.connection !== null && <li className="nav-item dropdown">
              <a
                href="#"
                className="nav-link dropdown-toggle notification-dropdown-toggle"
                id="navbarDropdownNotifiations"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                <SvgIcon icon={bellIcon} variant="solid" color={activeNotifications.length > 0 ? 'red' : 'rgba(255,255,255,.5)'} />
              </a>
              <div
                className="dropdown-menu notification-dropdown-menu"
                aria-labelledby="navbarDropdownNotifiations"
              >
                {activeNotifications.map((notification, index) => {
                  return (notification.PopupLink ?
                    <Link
                      key={index.toString()}
                      className="dropdown-item"
                      to={notification.Link}
                      onClick={() => openWindow(notification.PopupLink)}
                    >
                      {notification.Text}
                    </Link>
                    :
                    <Link
                      key={index.toString()}
                      className="dropdown-item"
                      to={notification.Link}
                    >
                      {notification.Text}
                    </Link>);
                })}
                {activeNotifications.length === 0 && (
                  <a key="none" className="dropdown-item disabled" href="">No Notifications</a>
                )}
                <div key="div2" className="dropdown-divider" />
                <Link key="all" className="dropdown-item" to={paths.AccountNotifications}>
                  View All Notifications
                </Link>
                {activeNotifications.length > 0 && [
                  <div key="div3" className="dropdown-divider" />,
                  <a
                    key="clear"
                    href=""
                    className="dropdown-item"
                    onClick={this.dismissAllNotifications}>
                    Clear All Notifications
                  </a>
                ]}
              </div>
            </li>}

            {/*<li className="nav-item dropdown">
              <a
                href="#"
                className="nav-link dropdown-toggle"
                id="navbarDropdownLoadBoard"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
                onDoubleClick={() => window.location.href = paths.LoadBoard}
              >
                Load Board
              </a>
              <div className="dropdown-menu" aria-labelledby="navbarDropdownLoadBoard">
                <Link className="dropdown-item" to={paths.LoadBoard}>Open Shipments</Link>
                <Link className="dropdown-item" to={paths.LoadBoardBidHistory}>Bid History</Link>
              </div>
            </li>*/}

            <li className="nav-item dropdown">
              <a
                href="#"
                className="nav-link dropdown-toggle"
                id="navbarDropdownTrack"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
                onDoubleClick={() => window.location.href = "/Track/Index"}
              >
                Track
              </a>
              <div className="dropdown-menu" aria-labelledby="navbarDropdownTrack">
                <Link className="dropdown-item" to="/Track/Index">Track</Link>
                <Link className="dropdown-item" to={paths.TrackPositions}>Positions</Link>
                <Link className="dropdown-item" to={paths.ScheduledOrders}>Scheduled</Link>
                <Link className="dropdown-item" to={paths.ShiftTurnover}>Shift Turnover</Link>
                <Link className="dropdown-item" to={paths.ServiceExceptions}>Service Exceptions</Link>
                <Link className="dropdown-item" to={paths.DeadHeadCreate}>DeadHead Trip</Link>
                <Link className="dropdown-item" to={paths.DeadHeadCreateHourlyDriver}>Hourly Driver Non-Billable Trip</Link>
              </div>
            </li>

            <li className="nav-item dropdown">
              <a
                href="#"
                className="nav-link dropdown-toggle"
                id="navbarDropdownQuote"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
                onDoubleClick={() => window.location.href = "/Quote/Index"}
              >
                Quote
              </a>
              <div className="dropdown-menu" aria-labelledby="navbarDropdownQuote">
                <Link className="dropdown-item" to="/Quote/Index">New Quote</Link>
                <Link className="dropdown-item" to={paths.QuoteHistory}>History</Link>
                <Link className="dropdown-item" to={paths.QuoteFinder}>Quote Finder</Link>
                <Link className="dropdown-item" to={paths.BlindBidQuote}>Blind Bid Quote</Link>
                <Link className="dropdown-item" to={paths.CarrierLoadBoard}>Carrier Load Board</Link>
                <Link className="dropdown-item" to={paths.SystemQuoteBids}>AI Control Tower</Link>
              </div>
            </li>

            <li className="nav-item dropdown">
              <a
                href="#"
                className="nav-link dropdown-toggle"
                id="navbarDropdownAssets"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                Assets
              </a>
              <div className="dropdown-menu" aria-labelledby="navbarDropdownAssets">
                <Link className="dropdown-item" to="/Assets/Drivers">Drivers</Link>
                <Link className="dropdown-item" to="/Assets/Vehicles">Vehicles</Link>
                <Link className="dropdown-item" to={paths.AssetTrailers}>Trailers</Link>
                <Link className="dropdown-item" to={paths.AssetOwners}>Owners</Link>
                <Link className="dropdown-item" to={paths.AssetsCarriers}>Carriers</Link>
                <Link className="dropdown-item" to={paths.CarrierContactsGrid}>Carrier Contacts</Link>
                <Link className="dropdown-item" to={paths.CarrierGroups}>Carrier Groups</Link>
                <Link className="dropdown-item" to={paths.FleetMessages}>Fleet Messages</Link>
                <Link className="dropdown-item" to="/Assets/DriverScoreDisputes">Driver Score Disputes</Link>
                <Link className="dropdown-item" to="/Assets/CarrierScoreDisputes">Carrier Score Disputes</Link>
                <Link className="dropdown-item" to={paths.WorkloadHistory}>Workload History</Link>
                <Link className="dropdown-item" to="/Assets/Inspections">Inspections</Link>
                <Link className="dropdown-item" to={paths.FreightClaims}>Cargo Claims</Link>
                <Link className="dropdown-item" to="/Driver/Chat">Driver Chat</Link>
                <Link className="dropdown-item" to={paths.DriverAccidents}>Accidents / Incidents</Link>
                <Link className="dropdown-item" to={paths.LoadMap}>Load Map</Link>
                <Link className="dropdown-item" to={paths.VirtualTerminals}>Virtual Terminals</Link>
                <Link className="dropdown-item" to={paths.ComplianceDates}>Compliance Dates</Link>
                <Link className="dropdown-item" to={paths.DriverCardLogs}>Card Logs</Link>
                <Link className="dropdown-item" to={paths.UploadBestpassTollReport}>Upload Bestpass Toll Report</Link>
                <Link className="dropdown-item" to={paths.DriverMetrics}>Driver Metrics</Link>
                <Link className="dropdown-item" to={paths.CashAdvanceRequests}>Cash Advance Requests</Link>
                <Link className="dropdown-item" to={paths.EquipmentCounts}>Equipment Counts</Link>
              </div>
            </li>

            <li className="nav-item dropdown">
              <a
                href="#"
                className="nav-link dropdown-toggle"
                id="navbarDropdownCustomers"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
                onDoubleClick={() => window.location.href = paths.Customers}
              >
                Customers
              </a>
              <div className="dropdown-menu" aria-labelledby="navbarDropdownCustomers">
                <Link className="dropdown-item" to={paths.Customers}>Customers</Link>
                <Link className="dropdown-item" to={paths.CustomerContactsGrid}>Customer Contacts</Link>
                <Link className="dropdown-item" to={paths.Salespeople}>Salespeople</Link>
                <Link className="dropdown-item" to={paths.CustomsBrokers}>Customs Brokers</Link>
                <Link className="dropdown-item" to={paths.EmailAliases}>Email Aliases</Link>
                <Link className="dropdown-item" to={paths.RateMasterViewer}>Tariff Rate Structures</Link>
                <Link className="dropdown-item" to={paths.CustomersMerge}>Merge Customers</Link>
                <Link className="dropdown-item" to={paths.CustomersMassUpdateSalespeople}>Mass Update Salespeople</Link>
                <Link className="dropdown-item" to={paths.CustomersBackhaul}>Backhaul Assistant</Link>
                <Link className="dropdown-item" to={paths.CustomerCreditScore}>Credit Check</Link>
              </div>
            </li>

            <li className="nav-item dropdown">
              <a
                href="#"
                className="nav-link dropdown-toggle"
                id="navbarDropdownBilling"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                Billing
              </a>
              <div className="dropdown-menu" aria-labelledby="navbarDropdownBilling">
                <Link className="dropdown-item" to={paths.InvoiceListing}>Invoice Listing</Link>
                <Link className="dropdown-item" to={paths.PostInvoiceBatch}>Post Invoice Batch</Link>
                <Link className="dropdown-item" to={paths.DownloadInvoiceBatch}>Download Invoice Batch</Link>
                <Link className="dropdown-item" to={paths.InvoiceGateway}>Invoice Gateway</Link>
              </div>
            </li>

            {this.state.links.find(x => x.Name == "AccountsReceivableCollections") && <li className="nav-item dropdown">
              <a
                href="#"
                className="nav-link dropdown-toggle"
                id="navbarDropdownAccountsReceivable"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
                onDoubleClick={() => window.location.href = paths.AccountsReceivableCollections}
              >
                A/R
              </a>
              <div className="dropdown-menu" aria-labelledby="navbarDropdownAccountsReceivable">
                <Link className="dropdown-item" to={paths.AccountsReceivableCollections}>Collections</Link>
              </div>
            </li>}

            <li className="nav-item dropdown">
              <a
                href="#"
                className="nav-link dropdown-toggle"
                id="navbarDropdownSettlements"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                Settlements
              </a>
              <div className="dropdown-menu" aria-labelledby="navbarDropdownSettlements">
                <Link className="dropdown-item" to={paths.PayListing}>Pay Listing</Link>
                <Link className="dropdown-item" to={paths.DeductionCOAs}>Deduction Types &amp; COA Mapping</Link>
                <Link className="dropdown-item" to={paths.ScheduledDeductions}>Deduction Scheduler</Link>
                <Link className="dropdown-item" to={paths.GenerateDeductions}>Pay Periods, Sheets &amp; Deduction Tracker</Link>
                <Link className="dropdown-item" to={paths.OwnerDiscrepancies}>Owner Discrepancies</Link>
                <Link className="dropdown-item" to={paths.PostPayBatch}>Post Pay Batch</Link>
                <Link className="dropdown-item" to={paths.PayTripCreate}>Additional Pay Trip</Link>
                <Link className="dropdown-item" to={paths.UploadVehicleFuelDeductions}>Upload Vehicle Fuel Deductions</Link>
                <Link className="dropdown-item" to={paths.UploadDriverChargebacks}>Upload Driver/Vehicle Chargebacks</Link>
                <Link className="dropdown-item" to={paths.UploadEmailOwnerFuelReport}>Upload/Email Owner Fuel Report</Link>
                <Link className="dropdown-item" to={paths.UploadAmortizedVehicleChargebacks}>Upload Amortized Vehicle Chargebacks</Link>
                {this.state.links.find(x => x.Name == "Owner1099Report") && <Link className="dropdown-item" to={paths.Owner1099Report}>Owner 1099 Report</Link>}
                {this.state.links.find(x => x.Name == "Owner1042Report") && <Link className="dropdown-item" to={paths.Owner1042Report}>Owner 1042 Report</Link>}
                <Link className="dropdown-item" to={paths.PayRateLinehaul}>Pay Rate Linehaul</Link>
                <Link className="dropdown-item" to={paths.PayRateAccessorial}>Pay Rate Accessorial</Link>
              </div>
            </li>

            {this.state.links.find(x => x.Name == "Zoom") && <li className="nav-item dropdown">
              <a
                href="#"
                className="nav-link dropdown-toggle"
                id="navbarDropdownZoom"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                Zoom
              </a>
              <div className="dropdown-menu" aria-labelledby="navbarDropdownZoom">
                <Link className="dropdown-item" to={paths.ZoomCallLogs}>Call Logs</Link>
                <Link className="dropdown-item" to={paths.ZoomOperationalLogs}>Operational Logs</Link>
              </div>
            </li>}

            <li className="nav-item dropdown">
              <a
                href="#"
                className="nav-link dropdown-toggle"
                id="navbarDropdownPurchaseOrder"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
                onDoubleClick={() => window.location.href = "/PurchaseOrder/Index"}
              >
                Purchase Orders
              </a>
              <div className="dropdown-menu" aria-labelledby="navbarDropdownPurchaseOrder">
                <Link className="dropdown-item" to="/PurchaseOrder/Index">Purchase Orders</Link>
                <Link className="dropdown-item" to={paths.OfficeSupplyRequests}>Office Supply Requests</Link>
                <Link className="dropdown-item" to="/PurchaseOrder/Vendors">Vendors</Link>
                <Link className="dropdown-item" to={paths.PurchaseOrderGeneralLedgerAccounts}>GL Accounts</Link>
              </div>
            </li>

            <li className="nav-item dropdown">
              <a
                href="#"
                className="nav-link dropdown-toggle"
                id="navbarDropdownShop"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
                onDoubleClick={() => window.location.href = paths.ShopWorkOrders}
              >
                Shop
              </a>
              <div className="dropdown-menu" aria-labelledby="navbarDropdownShop">
                <Link className="dropdown-item" to={paths.ShopWorkOrders}>Work Orders</Link>
                <Link className="dropdown-item" to={paths.ShopInventory}>Inventory</Link>
              </div>
            </li>

            <li className="nav-item dropdown">
              <a href="#" className="nav-link dropdown-toggle" data-toggle="dropdown" role="button" aria-haspopup="true" aria-expanded="false">3rd Party Sites<span className="caret"></span></a>
              <ul className="dropdown-menu">
                <li><a href="http://apt4.activeaero.com/Login/LoginForm.cfm" target="_blank" className="dropdown-item">Active Aero</a></li>
                <li><a href="https://acument.mercurygate.net/MercuryGate/login/mgLogin.jsp?inline=true&kick=1515689653255" target="_blank" className="dropdown-item">Acument</a></li>
                <li><a href="http://www.ardmorelogistics.com/inter/index.php/home" target="_blank" className="dropdown-item">Ardmore Power Logistics</a></li>
                <li><a href="https://web.cargobase.com/login/" target="_blank" className="dropdown-item">Cargobase (Continental Automotive)</a></li>
                <li><a href="http://truckers.exworks.com/" target="_blank" className="dropdown-item">Ex Works</a></li>
                <li><a href="https://ecco.expeditors.com/exp/zseries/appletStartupExternal.html" target="_blank" className="dropdown-item">Expeditors</a></li>
                <li><a href="https://gtms.glovisusa.com" target="_blank" className="dropdown-item">Glovis (IE Only)</a></li>
                <li><a href="http://apps.leanlogistics.com" target="_blank" className="dropdown-item">Leading Edge/Lean Logistics</a></li>
                <li><a href="https://web.menlolog.com/lmsweb/" target="_blank" className="dropdown-item">Menlo</a></li>
                <li><a href="https://otm.xpo.com/GC3/glog.webserver.servlet.umt.Login" target="_blank" className="dropdown-item">Menlo Worldwide Logistics</a></li>
                <li><a href="https://logon.onenetwork.com:443/sso/logon.sso" target="_blank" className="dropdown-item">One Networks - Piston/Dana</a></li>
                <li><a href="https://www.penskelogistics.net/csr/" target="_blank" className="dropdown-item">Penske - Whirlpool/BRP/MidwayZF/Toyota/Transfreight</a></li>
                <li><a href="https://pensketm.mercurygate.net/MercuryGate/login/mgLogin.jsp?inline=true" target="_blank" className="dropdown-item">Penske Logistics</a></li>
                <li><a href="https://rushscm.mercurygate.net/MercuryGate/login/mgLogin.jsp?inline=true" target="_blank" className="dropdown-item">Rush</a></li>
                <li><a href="http://sonoco10.shipcomm.com/Web/" target="_blank" className="dropdown-item">Sunoco</a></li>
                <li><a href="https://www.capacitygateway.com" target="_blank" className="dropdown-item">XPO - Capacity</a></li>
                <li><a href="https://www.expedite.xpo.com" target="_blank" className="dropdown-item">XPO - Expedite</a></li>
                <li><a href="https://specialized.xpo.com/carrier/Login.asp?login=1" target="_blank" className="dropdown-item">XPO - Specialized</a></li>
                <li><a href="https://yrc.freightcontroltower.com" target="_blank" className="dropdown-item">YRC</a></li>
              </ul>
            </li>

            {this.state.links.find(x => x.Name == "Hangfire") && <li className="nav-item dropdown">
              <a
                href="#"
                className="nav-link dropdown-toggle"
                id="navbarDropdownHangfire"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
                onDoubleClick={() => window.location.href = "/hangfire"}
              >
                Hangfire
              </a>
              <div className="dropdown-menu" aria-labelledby="navbarDropdownHangfire">
                <a className="dropdown-item" href="/hangfire">Dashboard</a>
                <a className="dropdown-item" href="/hangfire/jobs/enqueued">Jobs</a>
                <a className="dropdown-item" href="/hangfire/retries">Retries</a>
                <a className="dropdown-item" href="/hangfire/recurring?from=0&amp;count=1000">Recurring</a>
              </div>
            </li>}
          </ul>

        </div>
      </nav>
    </React.Fragment>
  }

  private openWhatsNew() {
    trackEvent('navbar.whats_new_opened');

    if (latestChangelogDate) {
      localStorage.setItem("WhatsNew_LastSeen", latestChangelogDate);
    }
    this.setState({ whatsNewOpen: true, whatsNewLastSeen: latestChangelogDate });
  }

  private createSignalRConnection() {

    // Create Connection
    this.connection = new HubConnectionBuilder()
      .withAutomaticReconnect(new LoadOneReconnectPolicy())
      .withUrl("/hub/notifications")
      .configureLogging(LogLevel.Warning)
      .build();

    this.connection.onreconnected(() => {
      this.props.requestAccountNotifications();
    });

    // On New Notification
    this.connection.on('addRange', (notifications: WebNotification[]) => {
      // HTML5 Audio
      for (let i = 0; i < notifications.length; i++) {
        if (notifications[i].Sound) {
          this.setState({ sound: notifications[i].Sound });
          break;
        }
      }
      // HTML5 Notification
      if ('Notification' in window) {
        Notification.requestPermission((status) => {
          if (status !== 'denied') {
            for (let i = 0; i < notifications.length; i++) {
              this.createHtmlNotification(notifications[i]);
            }
          }
        });
      }

      // Update Account Notifications
      this.props.requestAccountNotifications();
    });

    this.connection.on('refresh', () => {
      this.props.requestAccountNotifications();
    });

    // connect
    this.connection.start()
      .catch((error) => {
        console.error(error);
      });
  }

  private createHtmlNotification(notification: WebNotification) {
    new Notification(notification.Text, { tag: notification.Id })
      .onclick = () => {
        if (notification.PopupLink) {
          openWindow(notification.PopupLink);
          if (notification.Link)
            window.location.href = notification.Link;
        } else {
          window.focus();
          window.location.href = notification.Link;
        }
      };
  }

  private dismissAllNotifications(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    e.preventDefault();
    this.getActiveNotifications(this.props.accountNotifications).forEach((notification) => {
      const link = notification.Links.find(x => x.Name === 'Dismiss')
      if (link) {
        fetchApi(link.Link, {}, link.Method);
      }
    });
  }

  private dismissNotification(notification: AccountNotificationState.IAccountNotification | undefined): Promise<void> {
    const link = notification.Links.find(x => x.Name === 'Dismiss');
    if (!link) return Promise.resolve();

    return fetchApi(link.Link, {}, link.Method)
      .then(() => {
        this.props.requestAccountNotifications();
      })
      .catch((error) => {
        console.error(error);
      });
  }

  private openAndAcknowledgeNotification(notification: AccountNotificationState.IAccountNotification | undefined) {
    // Open popups synchronously (to avoid popup blockers)
    if (notification.PopupLink) {
      openWindow(notification.PopupLink);
    }
    this.dismissNotification(notification)
      .then(() => {
        if (notification.Link) {
          // TODO: react router navigation
          window.location.href = notification.Link;
        }
      });
  }

  private emailSupport(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    e.preventDefault();


    // Create Sentry Issue
    Sentry.withScope((scope) => {
      scope.setLevel('debug');

      const subject = `Support Request - ${this.state.username}`;
      const sentryId = Sentry.captureMessage(subject);
      const sentryUrl = `https://app-nouveau-canada.sentry.io/issues/?project=1825006&query=id%3A${sentryId}`;
      const body = `Please attach any screen shots if possible and indicate any and all identifiers below that apply to this issue that would help us determine the cause:

Module: 
Quote No: 
Order No: 
Truck No: 
Trailer ID: 
Driver ID: 
Owner ID: 
Customer ID: 
Carrier ID: 
Other Identifier: 
Nature of Problem: 
When did problem start occurring: 
Other Relevant Info: 

--------------------

The link below is used to assist tech. support with your issue.  Please do not remove it from this email.
${sentryUrl}`;
      window.location.href = `mailto:app@load1.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    });
  }
}

const ConnectedNavBar = connector(NavBar);
export default ConnectedNavBar;
